import React, { useEffect, useRef, useState } from "react";
import Shell from "@/components/Shell";
import RadioVideos from "@/components/RadioVideos";
import { useRadio } from "@/components/RadioProvider";
import PerformerOverlay from "@/components/PerformerOverlay";
import TrackTime from "@/components/TrackTime";
import { api, API } from "@/lib/api";
import {
    Loader2, Upload, Play, Pause, Square, Rewind, FastForward, Download, Trash2,
    Radio as RadioIcon, Mic, Volume2, VolumeX, Sparkles, FileDown, BarChart2,
    Cloud, CheckCircle2, AlertTriangle, Music, Film, Subtitles, Package,
} from "lucide-react";

/**
 * Radio v1 — station that mixes uploaded Mureka MP3s with rotating TTS "drops"
 * from the Ledger. One track → one drop (different voice each time) → next track.
 * Drops are read via the Web Speech API using voices tagged from the Personnel cast.
 */


// Iter 31.67 (May 2026) — Broadcast Pane. Always-visible pane on Radio.
// Six's accessibility canon: "deaf reads the radio, blind listens to the
// ledger". When a track is playing: lyrics (subtitle pane). When idle or
// between tracks: rolling bulletins from Two's filed comms. Cycles every
// 8s when bulletins mode is active. TTS toggle reads bulletins aloud for
// blind operators via browser Web Speech API (no cost, no extra key).
function BroadcastPane({ nowPlaying, showSubs, setShowSubs, subsLoading, subsByTrack, audioEl: localAudioEl, isPlaying }) {
    const isTrack = nowPlaying?.kind === "track";
    const isDrop = nowPlaying?.kind === "drop";
    const radio = useRadio();
    // Iter 31.132 fix — prefer the local Radio-page audio element passed
    // in via prop. Falls back to RadioProvider's (the nub) when this
    // component is used from a page other than /radio.
    const audioEl = localAudioEl || radio?.audioRef?.current || null;
    const musicPlaying = isPlaying ?? !!radio?.playing;
    const [bulletins, setBulletins] = useState([]);
    const [bulletinIdx, setBulletinIdx] = useState(0);
    const [bulletinLoading, setBulletinLoading] = useState(false);
    const [bulletinErr, setBulletinErr] = useState("");
    const [ttsOn, setTtsOn] = useState(false);
    // Iter 31.132 — readKey for PerformerOverlay. Increments on each
    // new bulletin so the espeak readout + jaw animation re-trigger.
    const [readKey, setReadKey] = useState(0);
    const ttsRef = useRef(null);

    // Pull recent comms inbound (Two's bulletins to Six) — 25 newest.
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setBulletinLoading(true);
            try {
                const { data } = await api.get("/comms/inbound?limit=25");
                if (!cancelled) setBulletins(data.items || []);
            } catch (e) {
                if (!cancelled) setBulletinErr(`[HTTP ${e?.response?.status || "?"}] ${e?.response?.data?.detail || e.message}`);
            } finally {
                if (!cancelled) setBulletinLoading(false);
            }
        };
        load();
        // Refresh every 60s so new bulletins from Two surface without reload.
        const t = setInterval(load, 60_000);
        return () => { cancelled = true; clearInterval(t); };
    }, []);

    // Iter 31.132e — auto-cycle bulletins every 8s WHEN TTS is OFF.
    // When ttsOn is true we advance via PerformerOverlay's onDone instead
    // so we never skip mid-readout. The timer is also paused during
    // drops (the page handles those) and during track playback (subtitle
    // mode owns the pane).
    useEffect(() => {
        if (isTrack || isDrop || bulletins.length === 0 || ttsOn) return;
        const t = setInterval(() => {
            setBulletinIdx((i) => (i + 1) % bulletins.length);
        }, 8000);
        return () => clearInterval(t);
    }, [isTrack, isDrop, bulletins.length, ttsOn]);

    // TTS — read current bulletin aloud when toggled on. Browser Web Speech.
    const cancelTTS = () => {
        try {
            if (typeof window !== "undefined" && window.speechSynthesis) {
                window.speechSynthesis.cancel();
            }
        } catch (e) {
            return;
        }
    };
    // Iter 31.132 — PerformerOverlay now owns the bulletin readout
    // (jaw tick + head-bob synchronised). When ttsOn flips or the
    // bulletin index changes, bump readKey so the overlay re-triggers.
    //
    // Iter 31.174 — fix the "Furious Loop". Previously depended on
    // `bulletins` (whole array reference). The 60s poll at line 61
    // always calls `setBulletins(data.items || [])` which creates a
    // new array reference even when content is identical. React's
    // Object.is dep check then re-ran this effect every minute,
    // bumping readKey and forcing the espeak overlay to re-trigger
    // — same class of bug as iter 31.154's getUser reference churn.
    // Depend on the primitive `.length` and `bulletinIdx` instead;
    // they only change when something actually advances.
    useEffect(() => {
        if (!ttsOn || isTrack || bulletins.length === 0) {
            cancelTTS();
            return;
        }
        setReadKey((k) => k + 1);
        return () => cancelTTS();
    }, [ttsOn, isTrack, bulletins.length, bulletinIdx]);

    const currentBulletin = bulletins[bulletinIdx];

    return (
        <div
            className="paper-card border-2 border-stamp-red/30 p-4 mb-6"
            data-testid="radio-broadcast-pane"
        >
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Subtitles className="w-4 h-4 text-stamp-red" strokeWidth={1.6} />
                    <span className="label-typed tw-prefix">
                        {isTrack
                            ? "Subtitles · lyrics"
                            : isDrop
                                ? "Transcript · live drop"
                                : "Bulletins · Two's broadcast"}
                    </span>
                    {!isTrack && !isDrop && bulletins.length > 0 && (
                        <span className="label-typed text-[10px] text-ink-faint">
                            {bulletinIdx + 1} / {bulletins.length}
                        </span>
                    )}
                </div>
                <div className="flex gap-2">
                    {/* Iter 31.135 — bulletin prev/next nav removed.
                        Canon: "it can't be skipped" applies to the
                        broadcast surface too. Auto-cycle (TTS-gated)
                        is the only advancement path. */}
                    {!isTrack && !isDrop && (
                        <button
                            onClick={() => setTtsOn((v) => !v)}
                            data-testid="radio-bulletin-tts"
                            aria-pressed={ttsOn}
                            className={`font-heading uppercase text-[10px] tracking-[0.22em] px-2 py-1 border inline-flex items-center gap-1 ${ttsOn ? "border-stamp-red text-stamp-red bg-stamp-red/10" : "border-ink/20 text-ink-soft hover:border-stamp-red hover:text-stamp-red"}`}
                            title="Read bulletin aloud (browser TTS)"
                        >
                            <Volume2 className="w-3 h-3" strokeWidth={1.6} />
                            {ttsOn ? "speaking" : "speak"}
                        </button>
                    )}
                    <button
                        onClick={() => setShowSubs((s) => !s)}
                        data-testid="radio-subs-toggle"
                        className="font-heading uppercase text-[10px] tracking-[0.22em] text-ink-soft hover:text-stamp-red"
                    >
                        {showSubs ? "Hide" : "Show"}
                    </button>
                </div>
            </div>

            {!showSubs ? null : isTrack ? (
                // Track mode: show lyrics for the playing song.
                subsLoading && !subsByTrack[nowPlaying.ref.id] ? (
                    <div className="flex items-center gap-2 text-ink-soft py-3">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        <span className="font-body text-xs">Loading lyrics…</span>
                    </div>
                ) : subsByTrack[nowPlaying.ref.id]?.has_lyrics ? (
                    <pre
                        className="font-body text-sm text-ink whitespace-pre-wrap leading-relaxed max-h-72 overflow-y-auto"
                        data-testid="radio-subtitles-text"
                    >
                        {subsByTrack[nowPlaying.ref.id].lyrics}
                    </pre>
                ) : (
                    <div className="font-body text-xs text-ink-faint italic py-2" data-testid="radio-subtitles-empty">
                        No lyrics on file for this track. Mureka-generated songs carry
                        them automatically; older tracks need backfill (Billing → Tools).
                    </div>
                )
            ) : isDrop ? (
                // Iter 31.132d — drop transcript. Two reads the drop via
                // Radio.jsx's speak() path; this surface shows the same
                // text on screen so deaf operators can read along (canon
                // iter 31.67). Domain pill + the literal text in monospace.
                <div data-testid="radio-drop-transcript">
                    <div className="flex items-baseline gap-2 mb-2 flex-wrap">
                        <span className="font-heading uppercase tracking-[0.22em] text-[11px] text-stamp-red">
                            Drop · {nowPlaying.ref.domain || "ledger"}
                        </span>
                        <span className="label-typed text-[10px] text-ink-faint">
                            two · live read
                        </span>
                    </div>
                    <p
                        className="font-body text-sm text-ink leading-relaxed max-h-72 overflow-y-auto"
                        data-testid="radio-drop-transcript-text"
                    >
                        {nowPlaying.ref.text || "(no drop text)"}
                    </p>
                </div>
            ) : bulletinLoading ? (
                <div className="flex items-center gap-2 text-ink-soft py-3">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    <span className="font-body text-xs">Loading bulletins…</span>
                </div>
            ) : bulletinErr ? (
                <pre className="font-body text-xs text-stamp-red whitespace-pre-wrap break-words py-2">{bulletinErr}</pre>
            ) : !currentBulletin ? (
                <div className="font-body text-xs text-ink-faint italic py-2" data-testid="radio-bulletin-empty">
                    No bulletins on file. Two will file briefs here as the system runs.
                </div>
            ) : (
                <div data-testid="radio-bulletin-current">
                    <div className="flex items-start gap-3 mb-2">
                        <PerformerOverlay
                            imageUrl={`/agent-portraits/${
                                ["one","two","three","ten"].includes((currentBulletin.source_agent || "two").toLowerCase())
                                    ? (currentBulletin.source_agent || "two").toLowerCase()
                                    : "two"
                            }.png`}
                            text={ttsOn ? `${currentBulletin.title || currentBulletin.subject || "Bulletin"}. ${currentBulletin.body_md || currentBulletin.summary || ""}`.slice(0, 800) : null}
                            playKey={ttsOn ? readKey : 0}
                            muted={!ttsOn}
                            rate={0.95}
                            pitch={0.95}
                            audioElement={audioEl}
                            audioActive={musicPlaying}
                            onDone={() => {
                                // Iter 31.132e — advance only when TTS owns
                                // the readout AND we're still in bulletin
                                // mode AND there's more than one bulletin.
                                if (ttsOn && !isTrack && !isDrop && bulletins.length > 1) {
                                    setBulletinIdx((i) => (i + 1) % bulletins.length);
                                }
                            }}
                            altText={`${currentBulletin.source_agent || "two"} reading bulletin`}
                            className="w-16 h-16 overflow-hidden rounded-sm flex-shrink-0 border border-ink/15"
                        />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-baseline gap-2 mb-2 flex-wrap">
                                <span className="font-heading uppercase tracking-[0.22em] text-[11px] text-stamp-red">
                                    {currentBulletin.title || currentBulletin.subject || "(no subject)"}
                                </span>
                                <span className="label-typed text-[10px] text-ink-faint">
                                    {currentBulletin.source_agent || currentBulletin.source || "two"} · {(currentBulletin.received_at || "").slice(0, 16).replace("T", " ")}
                                </span>
                            </div>
                            <div className="font-body text-sm text-ink whitespace-pre-wrap leading-relaxed max-h-72 overflow-y-auto">
                                {currentBulletin.body_md || currentBulletin.summary || "(no body)"}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


export default function Radio() {
    const [tracks, setTracks] = useState([]);
    // Iter 31.132e — single audio source. The nub (RadioProvider) and
    // the /radio page each own their own <audio>. To stop dual-stream
    // playback we pause the nub the first time the page audio plays.
    const nubRadio = useRadio();
    // Iter 31.69 — Spy Radio independence. Default feed = station tracks
    // only. 'My tracks' toggle switches to operator's personal library.
    const [tabMode, setTabMode] = useState("station"); // "station" | "mine"
    // Iter 31.80 — brand filter chips (station tab only). null = "All".
    const [brandFilter, setBrandFilter] = useState(null);
    const [drops, setDrops] = useState([]);
    const [queue, setQueue] = useState([]); // interleaved: {kind:"track"|"drop", ref}
    const [idx, setIdx] = useState(0);
    const [playing, setPlaying] = useState(false);
    const [muted, setMuted] = useState(false);
    // Iter 31.121 — Six's TTS voice mode. Default "espeak" reverts to the
    // browser's raw default voice (on Debian + HP that's espeak —
    // "Stephen Hawking from the West Midlands"). Mode "upgraded" runs
    // the LANG_MAP picker that prefers higher-quality OS voices. Persisted
    // in localStorage so the choice survives reloads.
    const [voiceMode, setVoiceMode] = useState(() => {
        if (typeof window === "undefined") return "espeak";
        return window.localStorage.getItem("radio_voice_mode") || "espeak";
    });
    useEffect(() => {
        try { window.localStorage.setItem("radio_voice_mode", voiceMode); } catch (_) {}
    }, [voiceMode]);
    const [err, setErr] = useState("");
    const [nowPlaying, setNowPlaying] = useState(null);
    const audioRef = useRef(null);
    // Iter 31.132 fix — mirror the local audio DOM node into state once
    // it mounts, so child PerformerOverlay components get a stable
    // dependency they can re-render on. Refs alone don't trigger re-renders.
    const [audioEl, setAudioEl] = useState(null);
    useEffect(() => { setAudioEl(audioRef.current); }, []);
    // Iter 31.59 — subtitle pane state. Lyrics are loaded lazily on track
    // change; we cache by track_id to avoid re-fetching. Accessible (deaf
    // operator + Two who can't listen) and useful for the back-catalogue
    // Mureka songs Claude wrote but never heard.
    const [showSubs, setShowSubs] = useState(true);
    const [subsByTrack, setSubsByTrack] = useState({});
    const [subsLoading, setSubsLoading] = useState(false);
    // Iter 31.77/31.78 — Commission-all button state. Shows how many
    // catalogue prompts have NOT yet been commissioned so Six knows the
    // expected credit cost before he taps.
    const [catalogueSize, setCatalogueSize] = useState(0);
    const [commissionedCount, setCommissionedCount] = useState(0);
    const [commissioning, setCommissioning] = useState(false);
    const [commissionMsg, setCommissionMsg] = useState("");
    // Songs vs Videos tab (iter 27.2). Videos placeholder lives in
    // /components/RadioVideos.jsx. We keep the songs DOM mounted with
    // display:none when on Videos so playback survives the flip.
    const [tab, setTab] = useState("songs");
    // Lightweight video count for the tab badge — keeps the heavy list
    // fetch inside RadioVideos itself. Refreshes when tab is opened.
    const [videoCount, setVideoCount] = useState(null);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/radio/videos");
                if (!cancelled) setVideoCount((data.videos || []).length);
            } catch (_) { /* ignore — tab just hides the count */ }
        })();
        return () => { cancelled = true; };
    }, [tab]);

    // Mureka generate state
    const [genPrompt, setGenPrompt] = useState("dry, deadpan anthem, North-England baritone, 90 BPM, sparse guitar");
    const [genLyrics, setGenLyrics] = useState("");
    const [genTitle, setGenTitle] = useState("");
    const [genBusy, setGenBusy] = useState(false);
    const [generations, setGenerations] = useState([]);
    // Iter 31.136b — live performer preview for the generation form.
    // Debounced server-side resolution so Six sees which performer will
    // front the song BEFORE commissioning. Free, no LLM cost.
    const [genPerformerPreview, setGenPerformerPreview] = useState(null);
    useEffect(() => {
        if (!genPrompt.trim() && !genLyrics.trim() && !genTitle.trim()) {
            setGenPerformerPreview(null);
            return;
        }
        const t = setTimeout(async () => {
            try {
                const { data } = await api.post("/radio/performer/preview", {
                    title: genTitle || undefined,
                    style: genPrompt || undefined,
                    prompt: genPrompt || undefined,
                    lyrics: genLyrics || undefined,
                });
                setGenPerformerPreview(data);
            } catch (_) { setGenPerformerPreview(null); }
        }, 400);
        return () => clearTimeout(t);
    }, [genPrompt, genLyrics, genTitle]);
    const [bulletin, setBulletin] = useState(null);
    const playEmittedRef = useRef({}); // dedupe play events per queue position

    // ---------- Mureka Pull (iter 26.4) ----------
    // Two actions, one panel: "Reconcile" rakes any task in our records
    // that never landed in radio_tracks; "Import by URL" pulls a single
    // Mureka song id (from a share URL or a paste) for tracks generated
    // outside the Shed. Both call /api/radio/mureka/*.
    const [pullBusy, setPullBusy] = useState(false);
    const [pullReport, setPullReport] = useState(null);  // last reconcile result
    const [importRef, setImportRef] = useState("");
    const [importBusy, setImportBusy] = useState(false);
    const [importResult, setImportResult] = useState(null);

    const reconcileMureka = async () => {
        setPullBusy(true); setPullReport(null);
        try {
            const { data } = await api.post("/radio/mureka/reconcile");
            setPullReport(data);
            await load();   // refresh tracks table
        } catch (e) {
            setErr(`[HTTP ${e.response?.status}] ${e.response?.data?.detail || e.message}`);
        } finally { setPullBusy(false); }
    };
    const importMureka = async () => {
        const v = importRef.trim();
        if (!v) return;
        setImportBusy(true); setImportResult(null);
        try {
            const { data } = await api.post("/radio/mureka/import", { ref: v });
            setImportResult(data);
            setImportRef("");
            await load();
        } catch (e) {
            setImportResult({ action: "error", error: e.response?.data?.detail || e.message });
        } finally { setImportBusy(false); }
    };
    // ----------------------------------------------

    const load = async () => {
        const [t, d, g, b, cat] = await Promise.all([
            api.get("/radio/tracks"),
            api.get("/radio/drops", { params: { limit: 12 } }),
            api.get("/radio/generations"),
            api.get("/radio/bulletin").catch(() => ({ data: null })),
            api.get("/radio/station/catalogue").catch(() => ({ data: null })),
        ]);
        setTracks(t.data.tracks || []);
        setDrops(t.data.tracks?.length ? (d.data.drops || []) : []);
        setGenerations(g.data.tasks || []);
        setBulletin(b.data || null);
        // Iter 31.77/31.78 — surface catalogue stats for the
        // "Commission all" button: how many prompts vs how many tracks
        // have actually landed in the station feed.
        if (cat?.data) {
            setCatalogueSize(cat.data.count || 0);
        }
        const landedStation = (t.data.tracks || []).filter((x) => x.station === true).length;
        setCommissionedCount(landedStation);
    };
    useEffect(() => { load(); }, []);

    // Iter 31.77/31.78 — Commission whole catalogue (POST batch endpoint).
    // Calls /radio/station/commission-all which is idempotent on
    // catalogue_id, so already-commissioned prompts are skipped server-side.
    // Confirm dialog before firing so Six sees the credit estimate.
    const commissionAll = async () => {
        const estimate = Math.max(0, catalogueSize - commissionedCount);
        if (estimate === 0) return;
        const ok = window.confirm(
            `Commission ${estimate} new Spy Radio songs from the catalogue?\n\n` +
            `· Cost: ~${estimate} credits (1 brief credit per song)\n` +
            `· Dispatch: ~${Math.ceil((estimate * 5) / 60)} min (5s gap between Mureka submits)\n` +
            `· Mureka then renders each over 60–90s. Tracks land in the station feed as they complete.\n\n` +
            `Already-commissioned prompts are skipped automatically.`,
        );
        if (!ok) return;
        setCommissioning(true);
        setCommissionMsg("");
        try {
            const { data } = await api.post("/radio/station/commission-all");
            setCommissionMsg(data.message || `Queued ${data.queued} prompts.`);
            // Refresh tracks every 20s for the next 6 minutes so the
            // operator can watch the catalogue fill in real time.
            for (let i = 0; i < 18; i += 1) {
                // eslint-disable-next-line no-await-in-loop
                await new Promise((r) => setTimeout(r, 20000));
                // eslint-disable-next-line no-await-in-loop
                await load();
            }
        } catch (e) {
            setCommissionMsg(e.response?.data?.detail?.toString() || "Commission failed");
        } finally {
            setCommissioning(false);
        }
    };

    const emitEvent = async (trackId, type) => {
        try { await api.post(`/radio/tracks/${trackId}/event`, { type }); } catch { /* silent */ }
    };

    // Iter 31.69 — filter tracks by tab. Station feed (default) shows
    // station=true. "My tracks" shows the operator's personal library.
    // Iter 31.80 — additional brand filter on the Station tab.
    const visibleTracks = (tracks || []).filter((t) => {
        const tabPass = tabMode === "station" ? t.station === true : t.station !== true;
        if (!tabPass) return false;
        if (tabMode === "station" && brandFilter) {
            return t.brand === brandFilter;
        }
        return true;
    });

    // Iter 31.80 — counts per brand for the filter chips (computed off
    // the unfiltered station-tagged tracks so the totals don't change
    // as Six clicks between brands).
    const brandCounts = (tracks || []).reduce((acc, t) => {
        if (t.station !== true) return acc;
        const b = t.brand || "unbranded";
        acc[b] = (acc[b] || 0) + 1;
        return acc;
    }, {});
    const stationTotal = Object.values(brandCounts).reduce((a, b) => a + b, 0);

    const exportLibrary = async () => {
        try {
            const r = await api.get("/radio/library/export", { responseType: "text" });
            const blob = new Blob([r.data], { type: "text/plain" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `station-library-${new Date().toISOString().slice(0, 10)}.txt`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            setErr(e.response?.data?.detail?.toString() || "Export failed");
        }
    };

    // Poll generations while any are in-flight
    useEffect(() => {
        const inflight = generations.some(
            (g) => !["saved", "failed", "error", "cancelled", "timeout", "no_url"].includes(g.status)
        );
        if (!inflight) return;
        const t = setInterval(load, 5000);
        return () => clearInterval(t);
    }, [generations]);

    // Re-build the interleaved queue whenever tracks, drops, or bulletin change.
    //   Bulletin → track[0] → drop[0] → track[1 % n] → drop[1] → track[2 % n] → drop[2] …
    // Length is max(tracks, drops) so every available drop voice gets a turn
    // before we repeat — even when only one track is loaded. Fixes No. 6's
    // "I haven't heard any different voices" report (13 May 2026): the old
    // loop indexed drops by TRACK index, so with one track only one drop
    // ever played.
    useEffect(() => {
        if (!visibleTracks.length) { setQueue([]); return; }
        const q = [];
        const bulletinDrop = bulletin?.text ? {
            kind: "drop",
            ref: {
                entry_id: "bulletin",
                domain: "bulletin",
                summary: bulletin.text,
                voice: bulletin.voice,
                text: bulletin.text,
            },
        } : null;
        if (bulletinDrop) q.push(bulletinDrop);
        const slots = Math.max(visibleTracks.length, drops.length, 1);
        for (let i = 0; i < slots; i += 1) {
            q.push({ kind: "track", ref: visibleTracks[i % visibleTracks.length] });
            if (drops.length) {
                q.push({ kind: "drop", ref: drops[i % drops.length] });
            }
        }
        setQueue(q);
    }, [tracks, drops, bulletin, tabMode]);

    const token = () => localStorage.getItem("msd_token");

    const remove = async (id) => {
        if (!window.confirm("Remove this track from the station?")) return;
        await api.delete(`/radio/tracks/${id}`);
        load();
    };

    // Iter 31.102 — Distributor handoff. Kicks off a background job
    // that builds a DistroKid/CD Baby ready bundle in Drive (master.mp3,
    // cover.png, lyrics, metadata.json, release-checklist.md).
    const prepDistribution = async (id, title) => {
        if (!window.confirm(
            `Prep distribution bundle for "${title}"?\n\n` +
            `This uploads master MP3 + freshly-generated cover + lyrics + ` +
            `ISRC-ready metadata.json + checklist.md to a dated folder ` +
            `under _Distribute/ in Drive. Cover generation uses ` +
            `Emergent credits (~1-2 per track).`
        )) return;
        try {
            const { data } = await api.post(`/radio/tracks/${id}/prep-distribution`);
            setErr(""); // clear prior errors
            window.alert(
                `Bundle queued (job ${data.job_id.slice(0, 8)}…).\n\n` +
                `${data.note}\n\nCheck "/radio/distribution" or your Drive in ~30 seconds.`
            );
        } catch (e) {
            const status = e.response?.status;
            const detail = e.response?.data?.detail || e.message || "Distribution prep failed";
            setErr(`[HTTP ${status || "no-response"}] ${typeof detail === "string" ? detail : JSON.stringify(detail)}`);
        }
    };

    const generate = async () => {
        if (!genPrompt.trim()) return;
        setGenBusy(true);
        setErr("");
        try {
            await api.post("/radio/generate", {
                prompt: genPrompt.trim(),
                lyrics: genLyrics.trim() || null,
                title: genTitle.trim() || "Mureka track",
            });
            setGenTitle("");
            setGenLyrics("");
            load();
        } catch (e) {
            const status = e.response?.status;
            const detail = e.response?.data?.detail || e.message || "Generation failed";
            setErr(`[HTTP ${status || "no-response"}] ${typeof detail === "string" ? detail : JSON.stringify(detail)}`);
        } finally {
            setGenBusy(false);
        }
    };

    const copyError = async () => {
        try { await navigator.clipboard.writeText(err); } catch { /* no-op */ }
    };

    // ---------------------------------------------------------------
    // VOICE PICKER — fixed in iter 22 (13 May 2026).
    //
    // The backend cast labels are two-word strings like "English male",
    // "English female", "American male", "Indian female", "Australian
    // female" (plus the German oompah special). The previous parser did:
    //     const [lang, , gender] = label.split(/\s+/);
    // which destructured ["english","male"] into 3 slots — so `lang` was
    // "english" and `gender` was undefined. SpeechSynthesisVoice.lang is
    // a BCP-47 tag like "en-GB" which never starts with "english", so the
    // match always failed and every drop fell back to voices[0]. Bug
    // surfaced as "I haven't heard any different voices" (No. 6, 13 May).
    //
    // Web Speech doesn't expose voice gender directly; we lean on
    // commonly-named voices across Chrome/Safari/Edge engines.
    // ---------------------------------------------------------------
    const LANG_MAP = {
        english: "en-gb", american: "en-us", indian: "en-in",
        australian: "en-au", french: "fr", german: "de",
    };
    const FEMALE_HINTS = /female|samantha|karen|moira|fiona|tessa|veena|allison|susan|victoria|amy|joanna|salli|kendra|fenella|kate|serena|catherine|zoe|aria|jenny|nora/i;
    const MALE_HINTS = /\bmale\b|daniel|oliver|alex|fred|tom|jorge|ralph|aaron|matthew|brian|joey|justin|george|arthur|guy/i;

    const pickVoice = (voiceLabel) => {
        const voices = window.speechSynthesis.getVoices();
        if (!voices.length) return null;
        const label = (voiceLabel || "English male").toLowerCase();
        const firstWord = label.split(/\s+/)[0];
        const langPrefix = LANG_MAP[firstWord] || "en";
        const wantsFemale = /female/.test(label);
        const wantsMale = /\bmale\b/.test(label) && !wantsFemale;
        const baseLang = langPrefix.split("-")[0]; // "en" / "fr" / "de"
        // Tier 1: exact BCP-47 region match (en-gb-*, en-us-*, …)
        let pool = voices.filter((v) => v.lang.toLowerCase().startsWith(langPrefix));
        // Tier 2: broader same-language pool if the region pool was empty
        if (!pool.length) pool = voices.filter((v) => v.lang.toLowerCase().startsWith(baseLang));
        // Tier 3: anything
        if (!pool.length) pool = voices;
        if (wantsFemale) {
            const f = pool.find((v) => FEMALE_HINTS.test(v.name));
            if (f) return f;
        }
        if (wantsMale) {
            const m = pool.find((v) => MALE_HINTS.test(v.name));
            if (m) return m;
        }
        return pool[0];
    };

    const speak = (text, voiceLabel, onDone) => {
        if (!("speechSynthesis" in window)) { onDone?.(); return; }
        window.speechSynthesis.cancel();
        const u = new SpeechSynthesisUtterance(text);
        // Iter 31.121 — espeak mode skips the LANG_MAP picker entirely
        // and lets the browser pick its raw default. On Debian without
        // Google voices that's espeak (Hawking-Brummie). Pitch flat, no
        // female lift, slightly slower rate to match espeak cadence.
        if (voiceMode === "espeak") {
            u.rate = 0.92;
            u.pitch = 1.0;
            u.volume = muted ? 0 : 0.9;
            // Explicitly do NOT set u.voice — browser default wins.
        } else {
            const wantsFemale = /female/i.test(voiceLabel || "");
            u.rate = 0.95;
            // Lift the pitch for female voices so they actually read as such — the
            // previous fixed 0.9 pitched everything down into the same baritone.
            u.pitch = wantsFemale ? 1.08 : 0.92;
            u.volume = muted ? 0 : 0.9;
            const picked = pickVoice(voiceLabel);
            if (picked) u.voice = picked;
        }
        let finished = false;
        const done = () => { if (!finished) { finished = true; onDone?.(); } };
        u.onend = done;
        u.onerror = done;
        // Safety net: if the engine never fires onend (Safari quirk on long
        // text), force completion after a generous ceiling so the queue
        // doesn't stall on a silent drop.
        setTimeout(done, Math.max(8000, text.length * 90));
        window.speechSynthesis.speak(u);
    };

    const playAt = (i) => {
        if (!queue.length) return;
        const pos = ((i % queue.length) + queue.length) % queue.length;
        const prevPos = idx;
        const prevItem = queue[prevPos];
        setIdx(pos);
        const item = queue[pos];
        setNowPlaying(item);
        if (item.kind === "track") {
            // Fire play (or replay if the same track is being re-selected)
            const key = `${item.ref.id}`;
            const isReplay = playEmittedRef.current[key];
            emitEvent(item.ref.id, isReplay ? "replayed" : "played");
            playEmittedRef.current[key] = true;
            // Iter 31.135 — canon shift: "the radio becomes a radio. It
            // can be on or off, it can't be skipped." Skip removed. The
            // old emitEvent("skipped") call when prevItem !== item is
            // Iter 31.182 (03 Feb 2026) — Track loading is now handled
            // exclusively by the authenticated blob-stream useEffect below
            // (deps [idx, playing, queue, muted]). The inline src/play
            // assignment that used to live here was firing in parallel with
            // the effect: src=query-param-URL → play → effect swaps to
            // blob:src → browser treats src-change as a new media load →
            // track restarts from 0:00. One described it as "each track
            // starts then immediately restarts" — exactly that race.
            //
            // We just pause anything previously playing, kick subtitle
            // hydration, and let setPlaying(true) at the end of playAt
            // drive the effect to fetch + load + play the new track.
            if (audioRef.current) audioRef.current.pause();
            // Iter 31.59 — lazy-load subtitles for this track. Cached by id.
            if (!subsByTrack[item.ref.id]) {
                setSubsLoading(true);
                api.get(`/radio/tracks/${item.ref.id}/lyrics`)
                    .then(({ data }) => {
                        setSubsByTrack((m) => ({ ...m, [item.ref.id]: data }));
                    })
                    .catch(() => {
                        setSubsByTrack((m) => ({ ...m, [item.ref.id]: { has_lyrics: false } }));
                    })
                    .finally(() => setSubsLoading(false));
            }
        } else {
            // TTS drop — no audio element; speechSynthesis handles it
            if (audioRef.current) audioRef.current.pause();
            speak(item.ref.text, item.ref.voice?.label, () => playAt(pos + 1));
        }
        setPlaying(true);
    };

    const toggle = () => {
        if (!queue.length) return;
        if (!playing) {
            // Iter 31.132e — pause the nub so /radio is the sole audio source.
            if (nubRadio?.playing && typeof nubRadio.togglePlay === "function") {
                try { nubRadio.togglePlay(); } catch (_) { /* ignore */ }
            }
            playAt(idx);
        } else {
            audioRef.current?.pause();
            window.speechSynthesis?.cancel();
            setPlaying(false);
        }
    };

    // Iter 31.135 — skip() removed. Canon: "the radio becomes a radio.
    // It can be on or off, it can't be skipped." Replaced with stop +
    // rewind (restart current track).
    const stop = () => {
        audioRef.current?.pause();
        if (audioRef.current) {
            try { audioRef.current.currentTime = 0; } catch (_) { /* ignore */ }
        }
        window.speechSynthesis?.cancel();
        setPlaying(false);
    };

    const rewind = () => {
        // Restart current track from 0:00. Doesn't change queue index.
        if (audioRef.current && nowPlaying?.kind === "track") {
            try { audioRef.current.currentTime = 0; } catch (_) { /* ignore */ }
            if (!playing) {
                audioRef.current.play().catch(() => {});
                setPlaying(true);
            }
        }
    };

    // Iter 31.185 (03 Feb 2026) — Track-level media controls.
    // Canon: the RADIO can only be on/off (Rule 14), but a specific
    // TRACK on the media player can be rewound, forwarded, stopped,
    // or downloaded. The forward button is gated on nowPlaying.kind
    // === "track" so radio drops (TTS) still can't be skipped.
    const forward = () => {
        if (nowPlaying?.kind !== "track") return;
        if (idx + 1 < queue.length) {
            playAt(idx + 1);
        }
    };

    const downloadTrack = async () => {
        if (nowPlaying?.kind !== "track") return;
        try {
            const res = await fetch(
                `${API}/radio/tracks/${nowPlaying.ref.id}/stream`,
                { headers: { Authorization: `Bearer ${token()}` } },
            );
            if (!res.ok) throw new Error(`download ${res.status}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const title = nowPlaying.ref.title || nowPlaying.ref.id || "track";
            const safe = title.replace(/[^\w.-]+/g, "_");
            a.download = `${safe}.mp3`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e) {
            setErr(`download failed: ${e.message}`);
        }
    };

    // Token-auth on streaming: we can't attach Authorization header to a plain <audio src=...>,
    // so the request goes through axios → swap: use fetch + blob URL instead.
    useEffect(() => {
        const el = audioRef.current;
        if (!el) return;
        const onEnded = () => playAt(idx + 1);
        el.addEventListener("ended", onEnded);
        return () => el.removeEventListener("ended", onEnded);
    }, [idx, queue]);

    // Because <audio> can't use bearer tokens directly, we proxy via fetch→blob
    useEffect(() => {
        const el = audioRef.current;
        if (!el) return;
        const item = queue[idx];
        if (!item || item.kind !== "track" || !playing) return;
        let revoked = false;
        (async () => {
            try {
                // Iter 31.186 (03 Feb 2026) — Telemetry. Increment a
                // global play counter and a console.timeStamp so any
                // future double-play race (iter 31.182) would surface
                // as two markers within milliseconds in Performance.
                try {
                    if (typeof window !== "undefined") {
                        window.__shedRadioPlays = (window.__shedRadioPlays || 0) + 1;
                        window.__shedRadioLastPlay = {
                            track_id: item.ref.id,
                            title: item.ref.title || null,
                            at: Date.now(),
                            idx,
                        };
                        if (window.performance?.mark) {
                            window.performance.mark(`shed-radio-play-${window.__shedRadioPlays}`);
                        }
                        // Surface to the DevTools timeline for spotting races.
                        // eslint-disable-next-line no-console
                        if (console && console.timeStamp) console.timeStamp(`shed-radio-play ${item.ref.id}`);
                    }
                } catch (_) { /* telemetry must never break playback */ }
                const res = await fetch(`${API}/radio/tracks/${item.ref.id}/stream`, {
                    headers: { Authorization: `Bearer ${token()}` },
                });
                if (!res.ok) throw new Error(`stream ${res.status}`);
                const blob = await res.blob();
                if (revoked) return;
                const url = URL.createObjectURL(blob);
                el.src = url;
                el.muted = muted;
                await el.play();
            } catch (e) {
                setErr(`track playback failed: ${e.message}`);
                setPlaying(false);
            }
        })();
        return () => { revoked = true; };
    }, [idx, playing, queue, muted]);

    useEffect(() => {
        if (audioRef.current) audioRef.current.muted = muted;
    }, [muted]);

    return (
        <Shell>
            <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-typed tw-prefix">Broadcast Hallway · Live Station</div>
                    <h1 className="font-heading text-5xl uppercase tracking-[0.04em] text-ink mt-1 flex items-center gap-3">
                        <RadioIcon className="w-10 h-10 text-stamp-red" strokeWidth={1.5} />
                        The Radio<span className="cursor-blink" />
                    </h1>
                    <p className="font-body text-sm text-ink-soft mt-3 max-w-2xl">
                        Upload Mureka tracks. The station alternates them with short ledger drops read
                        in a rotating voice from the cast. Always on, until No. 6 says otherwise.
                    </p>
                </div>
                <div className="pencil text-lg transform -rotate-2">
                    "Tonight's voice: whichever arrives." — No. 2
                </div>
            </div>

            {/* Player panel */}
            <div className="paper-card tape-corner p-5 mb-6 flex flex-wrap items-center gap-4 tilt-l" data-testid="radio-panel">
                <audio ref={audioRef} style={{ display: "none" }} preload="none" />
                <button
                    onClick={toggle}
                    disabled={!queue.length}
                    className="w-14 h-14 border-2 border-stamp-red text-stamp-red rounded-full flex items-center justify-center hover:bg-stamp-red hover:text-paper-light disabled:opacity-40"
                    data-testid="radio-play"
                >
                    {playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                </button>
                {/* Iter 31.135 — stop (pause + reset to 0:00). Stays on
                    the radio bar because stop = "radio off", which is
                    canonical (Rule 14: on or off, never skipped). */}
                <button
                    onClick={stop}
                    disabled={!queue.length || !playing}
                    className="w-10 h-10 border border-ink/30 text-ink-soft rounded-full flex items-center justify-center hover:text-stamp-red disabled:opacity-40"
                    data-testid="radio-stop"
                    title="Stop"
                >
                    <Square className="w-4 h-4" fill="currentColor" />
                </button>
                {/* Iter 31.185 (03 Feb 2026) — Per-track media controls.
                    Canon: the radio is on/off, but a track on the media
                    player can be rewound, forwarded, stopped, or
                    downloaded. Whole group only renders when a TRACK is
                    currently playing — radio drops (TTS) skip the bar
                    entirely so the canon Rule 14 firewall holds. */}
                {nowPlaying?.kind === "track" && (
                    <div
                        className="flex items-center gap-2 pl-3 ml-1 border-l border-ink/20"
                        data-testid="radio-track-media-bar"
                    >
                        <button
                            onClick={rewind}
                            disabled={!queue.length}
                            className="w-10 h-10 border border-ink/30 text-ink-soft rounded-full flex items-center justify-center hover:text-stamp-red disabled:opacity-40"
                            data-testid="radio-track-rewind"
                            title="Rewind — restart current track"
                        >
                            <Rewind className="w-4 h-4" />
                        </button>
                        <button
                            onClick={forward}
                            disabled={!queue.length || idx + 1 >= queue.length}
                            className="w-10 h-10 border border-ink/30 text-ink-soft rounded-full flex items-center justify-center hover:text-stamp-red disabled:opacity-40"
                            data-testid="radio-track-forward"
                            title="Forward — skip to next track"
                        >
                            <FastForward className="w-4 h-4" />
                        </button>
                        <button
                            onClick={downloadTrack}
                            disabled={!queue.length}
                            className="w-10 h-10 border border-ink/30 text-ink-soft rounded-full flex items-center justify-center hover:text-stamp-red disabled:opacity-40"
                            data-testid="radio-track-download"
                            title="Download track as .mp3"
                        >
                            <Download className="w-4 h-4" />
                        </button>
                    </div>
                )}
                <button
                    onClick={() => setMuted((m) => !m)}
                    className="w-10 h-10 border border-ink/30 text-ink-soft rounded-full flex items-center justify-center hover:text-stamp-red"
                    data-testid="radio-mute"
                >
                    {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
                <button
                    onClick={() => setVoiceMode((m) => m === "espeak" ? "upgraded" : "espeak")}
                    className={`h-10 px-2 border rounded-full flex items-center justify-center text-[10px] tracking-[0.15em] uppercase font-mono hover:text-stamp-red ${
                        voiceMode === "espeak"
                            ? "border-stamp-red/60 text-stamp-red"
                            : "border-ink/30 text-ink-soft"
                    }`}
                    title={
                        voiceMode === "espeak"
                            ? "Voice: system default (espeak on Debian — flat & robotic). Click to upgrade."
                            : "Voice: upgraded (LANG_MAP picker). Click to revert to system default."
                    }
                    data-testid="radio-voice-mode"
                >
                    {voiceMode === "espeak" ? "ESPEAK" : "UPGRADED"}
                </button>
                <div className="flex-1 min-w-[200px] max-w-[520px] flex items-start gap-3">
                    {/* Iter 31.132 fix — voice-sync clip in the ON AIR window.
                        Iter 31.136 — performer agent now derives from
                        the playing track's metadata (male/female/nb/group)
                        rather than hard-coding Twenty. */}
                    <PerformerOverlay
                        imageUrl={`/agent-portraits/${
                            (nowPlaying?.kind === "track" && nowPlaying?.ref?.performer?.key)
                                ? nowPlaying.ref.performer.key
                                : "twenty"
                        }.png`}
                        audioElement={audioEl}
                        audioActive={playing && nowPlaying?.kind === "track"}
                        visemeTrack={
                            nowPlaying?.kind === "track"
                                ? (nowPlaying.ref.viseme_track || null)
                                : null
                        }
                        muted
                        playKey={0}
                        altText={
                            nowPlaying?.kind === "track" && nowPlaying?.ref?.performer?.key
                                ? `performer — ${nowPlaying.ref.performer.key.replace(/_/g, " ")}`
                                : "performer — Agent Twenty"
                        }
                        className="w-16 h-16 overflow-hidden rounded-sm flex-shrink-0 border border-ink/15"
                    />
                    {/* Iter 31.132c — studio behind glass. When a drop is
                        on (Two reads a ledger entry via Radio.jsx's own
                        speak() path), her portrait appears beside
                        Twenty's, force-reading state on (jaw-flicker +
                        ON AIR pill), so the operator sees who's actually
                        on the mic. PerformerOverlay does NOT own the
                        TTS here — Radio.jsx's speak() does — so we use
                        forceReading=true to paint the visual without
                        double-speaking. */}
                    {nowPlaying?.kind === "drop" && (
                        <PerformerOverlay
                            imageUrl="/agent-portraits/two.png"
                            forceReading
                            muted
                            playKey={0}
                            altText="bulletin announcer — Agent Two"
                            className="w-16 h-16 overflow-hidden rounded-sm flex-shrink-0 border border-ink/15"
                        />
                    )}
                    <div className="flex-1 min-w-0">
                    <div className="label-typed tw-prefix flex items-center gap-2">
                        Now playing
                        {playing && (
                            <span
                                className="font-heading uppercase text-[9px] tracking-[0.25em] text-stamp-red border border-stamp-red px-1.5 py-0.5 animate-pulse"
                                data-testid="radio-on-air-pill"
                            >
                                ON AIR
                            </span>
                        )}
                        {nowPlaying?.kind === "drop" && (
                            <span
                                className="stamp-red text-[9px] border border-stamp-red px-1 tracking-[0.2em]"
                                data-testid="now-playing-drop-badge"
                            >
                                DROP · {nowPlaying.ref.voice?.label || "voice"}
                            </span>
                        )}
                        {nowPlaying?.kind === "track" && (
                            <span
                                className="text-stamp-green text-[9px] border border-stamp-green/60 px-1 tracking-[0.2em]"
                                data-testid="now-playing-track-badge"
                            >
                                TRACK {Math.max(1, queue.slice(0, idx + 1).filter((q) => q.kind === "track").length)} / {tracks.length}
                            </span>
                        )}
                    </div>
                    <div
                        className="font-heading text-lg text-ink tracking-[0.04em] truncate"
                        title={nowPlaying?.kind === "track" ? nowPlaying.ref.title : undefined}
                    >
                        {nowPlaying ? (
                            nowPlaying.kind === "track"
                                ? nowPlaying.ref.title
                                : `Drop: ${nowPlaying.ref.domain}`
                        ) : queue.length ? "Standby — hit play" : "No tracks loaded"}
                    </div>
                    {nowPlaying?.kind === "drop" && (
                        <div className="font-body text-xs text-ink-soft mt-1 line-clamp-2">
                            <Mic className="w-3 h-3 inline mr-1" /> {nowPlaying.ref.text}
                        </div>
                    )}
                    {/* Iter 31.206 — elapsed / remaining time readout. */}
                    {nowPlaying && (
                        <div
                            className="font-mono text-[11px] text-ink-soft mt-2"
                            data-testid="radio-page-track-time-wrap"
                        >
                            <TrackTime audioElement={audioRef.current} testId="radio-page-track-time" />
                        </div>
                    )}
                    </div>
                </div>
                <div className="label-typed">
                    {queue.length} in queue · {tracks.length} track{tracks.length === 1 ? "" : "s"} · {drops.length} drop{drops.length === 1 ? "" : "s"}
                </div>
            </div>

            {/* Iter 31.135 — "Coming up on Spy Radio". Shows the next
                3 queue items so the operator knows what's queued. Pure
                read-only — no clickthrough, no skip-to, canon: radio
                can't be skipped. */}
            {queue.length > 1 && (
                <div
                    className="paper-card tape-corner p-4 mb-6 tilt-r"
                    data-testid="radio-coming-up"
                >
                    <div className="font-heading uppercase tracking-[0.25em] text-[10px] text-stamp-red mb-2">
                        Coming up on Spy Radio
                    </div>
                    <ol className="space-y-1.5">
                        {Array.from({ length: 3 }).map((_, n) => {
                            const upIdx = (idx + 1 + n) % queue.length;
                            // Don't wrap back into nowPlaying.
                            if (upIdx === idx) return null;
                            const item = queue[upIdx];
                            if (!item) return null;
                            const key = `${item.kind}-${upIdx}-${item.ref?.id || item.ref?.entry_id || n}`;
                            const isTrack = item.kind === "track";
                            return (
                                <li
                                    key={key}
                                    data-testid={`radio-coming-up-item-${n}`}
                                    className="flex items-baseline gap-2 font-body text-sm"
                                >
                                    <span className="font-heading text-[10px] tracking-[0.18em] text-ink-faint w-5 flex-shrink-0">
                                        {String(n + 1).padStart(2, "0")}
                                    </span>
                                    <span
                                        className={`font-heading uppercase text-[9px] tracking-[0.22em] px-1.5 py-0.5 border flex-shrink-0 ${
                                            isTrack
                                                ? "border-stamp-green/60 text-stamp-green"
                                                : "border-stamp-red text-stamp-red"
                                        }`}
                                    >
                                        {isTrack ? "TRACK" : "DROP"}
                                    </span>
                                    <span className="text-ink truncate">
                                        {isTrack
                                            ? (item.ref.title || item.ref.filename || "Untitled")
                                            : (item.ref.domain
                                                ? `${item.ref.domain}: ${(item.ref.text || "").slice(0, 80)}`
                                                : (item.ref.text || "").slice(0, 100))}
                                    </span>
                                </li>
                            );
                        })}
                    </ol>
                </div>
            )}

            {/* Iter 31.59/31.67 — Broadcast Pane. Always visible.
                When a Mureka track plays: lyrics. Otherwise: rolling bulletins
                feed (Two's filed comms). Accessibility canon (iter 31.67):
                deaf operators can READ the radio; bulletin text speaks at the
                pace of the operator's eye, never lost to the listener. */}
            <BroadcastPane
                nowPlaying={nowPlaying}
                showSubs={showSubs}
                setShowSubs={setShowSubs}
                subsLoading={subsLoading}
                subsByTrack={subsByTrack}
                audioEl={audioEl}
                isPlaying={playing}
            />

            {/* Tab strip — Songs (default) / Videos. Video playback is live (iter 31.2);
                Mureka video generation is the only thing still upstream. */}
            <div className="flex items-center gap-1 mb-6 border-b border-ink/15" data-testid="radio-tab-strip">
                <button
                    onClick={() => setTab("songs")}
                    data-testid="radio-tab-songs"
                    className={`inline-flex items-center gap-2 px-4 py-2 font-heading uppercase tracking-[0.22em] text-[11px] border-b-2 transition-colors ${
                        tab === "songs"
                            ? "border-stamp-red text-stamp-red"
                            : "border-transparent text-ink-soft hover:text-ink"
                    }`}
                >
                    <Music className="w-3 h-3" strokeWidth={1.6} />
                    Songs
                    <span className="text-[9px] text-ink-soft tracking-normal">· {tracks.length}</span>
                </button>
                <button
                    onClick={() => setTab("videos")}
                    data-testid="radio-tab-videos"
                    className={`inline-flex items-center gap-2 px-4 py-2 font-heading uppercase tracking-[0.22em] text-[11px] border-b-2 transition-colors ${
                        tab === "videos"
                            ? "border-stamp-red text-stamp-red"
                            : "border-transparent text-ink-soft hover:text-ink"
                    }`}
                >
                    <Film className="w-3 h-3" strokeWidth={1.6} />
                    Videos
                    {videoCount != null && (
                        <span className="text-[9px] text-ink-soft tracking-normal">· {videoCount}</span>
                    )}
                </button>
            </div>

            {/* Videos tab content — playback live, click any tile to watch. */}
            {tab === "videos" && <RadioVideos />}

            {/* Songs surface — kept mounted with display:none when on Videos so playback survives. */}
            <div style={{ display: tab === "songs" ? "block" : "none" }} data-testid="radio-songs-surface">

            {/* Upload + track list */}
            <div className="grid grid-cols-12 gap-6">
                <div className="col-span-12 lg:col-span-5 space-y-4">
                    {/* Mureka generate */}
                    <div className="paper-card tape-corner p-5 tilt-l" data-testid="mureka-panel">
                        <div className="flex items-center gap-2 label-typed tw-prefix mb-2">
                            <Sparkles className="w-3 h-3" />
                            Mureka · generate a track
                        </div>
                        <label className="label-typed block mb-1">Title</label>
                        <input
                            type="text"
                            value={genTitle}
                            onChange={(e) => setGenTitle(e.target.value)}
                            placeholder="Song of the Week — BJLIAM/06"
                            className="w-full px-3 py-2 text-sm font-body mb-3"
                            data-testid="mureka-title"
                        />
                        <label className="label-typed block mb-1">Style / mood</label>
                        <textarea
                            rows={2}
                            value={genPrompt}
                            onChange={(e) => setGenPrompt(e.target.value)}
                            className="w-full px-3 py-2 text-sm font-body mb-3"
                            data-testid="mureka-prompt"
                        />
                        {/* Iter 31.136b — live performer preview. Resolved
                            server-side from the in-progress form fields
                            so Six sees who'll front the track before
                            committing the credits. */}
                        {genPerformerPreview && (
                            <div
                                className="mb-3 px-3 py-2 border border-ink/15 paper-card flex items-center gap-3"
                                data-testid="mureka-performer-preview"
                            >
                                <img
                                    src={`/agent-portraits/${genPerformerPreview.key}.png`}
                                    alt={`performer preview — ${genPerformerPreview.key}`}
                                    className="w-10 h-10 rounded-sm border border-ink/15 flex-shrink-0 object-cover"
                                    data-testid="mureka-performer-preview-img"
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="font-heading uppercase text-[10px] tracking-[0.22em] text-stamp-red">
                                        This prompt will perform as
                                    </div>
                                    <div className="font-body text-sm text-ink truncate">
                                        {genPerformerPreview.key.replace(/_/g, " ")}
                                        <span className="ml-2 label-typed text-[9px] text-ink-faint">
                                            · matched on {genPerformerPreview.source}
                                        </span>
                                    </div>
                                    {/* Iter 31.138 — credit visibility canon.
                                        Even when alignment is free, the
                                        cost preview surface stays so the
                                        operator's "users credit usage
                                        prior" rule applies uniformly. */}
                                    <div
                                        className="font-mono text-[10px] tracking-[0.18em] text-ink-faint mt-1"
                                        data-testid="mureka-align-cost"
                                    >
                                        ALIGNMENT · 0 CREDITS · ONE-TIME · HEURISTIC-V1
                                    </div>
                                </div>
                            </div>
                        )}
                        <label className="label-typed block mb-1">Lyrics (optional — use [Verse] / [Chorus])</label>
                        <textarea
                            rows={4}
                            value={genLyrics}
                            onChange={(e) => setGenLyrics(e.target.value)}
                            placeholder={"[Verse]\nGary has been knitting since 1987.\nHe has never been shot at.\n\n[Chorus]\nBe seeing you."}
                            className="w-full px-3 py-2 text-sm font-body mb-3"
                            data-testid="mureka-lyrics"
                        />
                        <button
                            onClick={generate}
                            disabled={genBusy || !genPrompt.trim()}
                            className="w-full bg-ink text-paper-light hover:bg-stamp-red disabled:opacity-50 px-4 py-2 font-heading uppercase text-xs tracking-[0.25em] inline-flex items-center justify-center gap-2"
                            data-testid="mureka-submit"
                        >
                            {genBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                            Commission track
                        </button>
                        <div className="label-typed mt-2 text-[9px]">
                            Typical turnaround 30–90s. Track appears in the library when finished.
                        </div>
                    </div>

                    {/* Generation queue */}
                    {generations.length > 0 && (
                        <div className="paper-card p-4" data-testid="mureka-queue">
                            <div className="label-typed tw-prefix mb-2">In production · {generations.length}</div>
                            <ul className="space-y-2">
                                {generations.slice(0, 6).map((g) => (
                                    <li key={g.id} className="flex items-start gap-2 text-xs font-body text-ink border-b border-ink/10 pb-2 last:border-0" data-testid={`gen-${g.id}`}>
                                        <span className={`label-typed text-[9px] pt-0.5 ${g.status === "saved" ? "text-stamp-green" : ["failed","error","timeout","no_url"].includes(g.status) ? "text-stamp-red" : "text-stamp-blue"}`}>
                                            {g.status}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <div className="truncate">{g.title}</div>
                                            <div className="label-typed text-[9px] truncate">{g.prompt}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Mureka Pull (iter 26.4) — reconcile our task history
                        AND let No. 6 paste in a Mureka share URL for tracks
                        generated outside the Shed. */}
                    <div className="paper-card p-5" data-testid="mureka-pull-panel">
                        <div className="label-typed tw-prefix mb-3 flex items-center gap-2">
                            <Cloud className="w-3 h-3 text-stamp-red" />
                            Pull from Mureka · gather what's not yet in the station
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                            <button
                                onClick={reconcileMureka}
                                disabled={pullBusy}
                                data-testid="mureka-reconcile-btn"
                                className="inline-flex items-center gap-2 border border-ink/40 hover:border-stamp-red hover:text-stamp-red px-4 py-2 font-heading uppercase text-xs tracking-[0.25em] disabled:opacity-50"
                            >
                                {pullBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RadioIcon className="w-3 h-3" />}
                                {pullBusy ? "Reconciling…" : "Reconcile generations"}
                            </button>
                            <span className="label-typed text-[9px] text-ink-soft">
                                checks every Mureka task we know of · imports anything finished but missing locally
                            </span>
                        </div>
                        {pullReport && (
                            <div className="label-typed text-[10px] mb-3 border-l-2 border-stamp-green pl-3" data-testid="mureka-reconcile-report">
                                {pullReport.tasks_examined} task{pullReport.tasks_examined === 1 ? "" : "s"} examined ·
                                {Object.entries(pullReport.by_action).map(([k, v]) => (
                                    <span key={k} className={`ml-2 ${k === "imported" ? "text-stamp-green" : k === "already-imported" ? "text-ink-soft" : "text-stamp-red"}`}>
                                        {v} {k}
                                    </span>
                                ))}
                            </div>
                        )}

                        <div className="border-t border-ink/10 pt-3 mt-3">
                            <div className="label-typed text-[10px] mb-2 text-ink-soft">
                                Generated in the Mureka app? Paste the share URL or song id below.
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                <input
                                    type="text"
                                    value={importRef}
                                    onChange={(e) => setImportRef(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") importMureka(); }}
                                    placeholder="https://mureka.ai/song/...  or  138200194678785"
                                    className="flex-1 min-w-[220px] px-3 py-2 text-sm font-body bg-transparent border-b border-ink/30 focus:border-stamp-red outline-none"
                                    data-testid="mureka-import-input"
                                />
                                <button
                                    onClick={importMureka}
                                    disabled={importBusy || !importRef.trim()}
                                    data-testid="mureka-import-btn"
                                    className="inline-flex items-center gap-2 bg-ink text-paper-light hover:bg-stamp-red px-4 py-2 font-heading uppercase text-xs tracking-[0.25em] disabled:opacity-50"
                                >
                                    {importBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />}
                                    Import
                                </button>
                            </div>
                            {importResult && (
                                <div
                                    className={`mt-3 text-xs font-body flex items-start gap-2 ${
                                        importResult.action === "imported" ? "text-stamp-green" :
                                        importResult.action === "already-imported" ? "text-ink-soft" :
                                        importResult.action === "not-ready" ? "text-stamp-blue" :
                                        "text-stamp-red"
                                    }`}
                                    data-testid="mureka-import-result"
                                >
                                    {importResult.action === "imported" || importResult.action === "already-imported"
                                        ? <CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                        : <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />}
                                    <span>
                                        {importResult.action === "imported" && <>Filed: <span className="font-heading">{importResult.title}</span> ({(importResult.size_bytes / 1024).toFixed(0)} KB)</>}
                                        {importResult.action === "already-imported" && <>Already in the station — track {importResult.track_id?.slice(0, 8)}</>}
                                        {importResult.action === "not-ready" && <>Mureka says status: {importResult.status}. Try again in a minute.</>}
                                        {importResult.action === "no-url" && <>Mureka reports complete but no audio URL — odd. Open the task in their app and re-share.</>}
                                        {importResult.action === "not-found" && <>Mureka doesn't know that id.</>}
                                        {importResult.action === "error" && <>{importResult.error}</>}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* One Entrance Rule (iter 25, 13 May 2026) — the standalone
                        MP3 upload was retired. Audio dropped into the Shredder
                        is detected by MIME/extension and routed to GridFS
                        natively. This banner reminds No. 6 where it now lives. */}
                    <div
                        className="paper-card border-2 border-dashed border-ink/30 p-5 text-center"
                        data-testid="radio-shredder-banner"
                    >
                        <Upload className="w-6 h-6 text-ink-soft mx-auto mb-2" strokeWidth={1} />
                        <div className="font-heading text-base uppercase tracking-[0.05em] text-ink-soft">
                            Audio enters via the Shredder
                        </div>
                        <div className="label-typed mt-1 text-[9px]">
                            One entrance, canon-enforced · MP3 · WAV · M4A · OGG · FLAC · max 25MB
                        </div>
                        <a
                            href="/shredder"
                            className="inline-block mt-3 border border-stamp-red text-stamp-red px-3 py-1.5 hover:bg-stamp-red hover:text-paper-light font-heading uppercase text-[10px] tracking-[0.25em]"
                            data-testid="radio-shredder-link"
                        >
                            Open Shredder
                        </a>
                    </div>
                    {err && (
                        <div className="paper-card border-2 border-stamp-red p-4" data-testid="radio-error">
                            <div className="flex items-center justify-between mb-2">
                                <div className="stamp-red font-heading text-sm tracking-[0.2em]">FAILURE ON FILE</div>
                                <div className="flex gap-2">
                                    <button onClick={copyError} className="border border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper-light px-3 py-1 font-heading uppercase text-[10px] tracking-[0.2em]" data-testid="radio-error-copy">Copy</button>
                                    <button onClick={() => setErr("")} className="border border-ink/30 hover:border-stamp-red hover:text-stamp-red px-3 py-1 font-heading uppercase text-[10px] tracking-[0.2em]" data-testid="radio-error-clear">Clear</button>
                                </div>
                            </div>
                            <pre className="font-body text-xs text-ink whitespace-pre-wrap break-words select-text">{err}</pre>
                            <div className="label-typed mt-2 text-[9px]">Stays until cleared.</div>
                        </div>
                    )}
                </div>

                <div className="col-span-12 lg:col-span-7 paper-card p-4 tilt-l">
                    <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                        <div className="flex items-center gap-2">
                            <div className="label-typed tw-prefix">
                                {tabMode === "station" ? "Station library" : "My tracks"} · {visibleTracks.length}
                            </div>
                            {/* Iter 31.69 — Spy Radio is now independent. Toggle
                                between the station's own catalogue and the operator's
                                personal library. */}
                            <div className="inline-flex border border-ink/20 rounded-none" data-testid="radio-tab-toggle">
                                <button
                                    onClick={() => setTabMode("station")}
                                    data-testid="radio-tab-station"
                                    className={`font-heading uppercase text-[10px] tracking-[0.22em] px-2 py-1 ${tabMode === "station" ? "bg-stamp-red text-paper" : "text-ink-soft hover:text-stamp-red"}`}
                                >
                                    Station
                                </button>
                                <button
                                    onClick={() => setTabMode("mine")}
                                    data-testid="radio-tab-mine"
                                    className={`font-heading uppercase text-[10px] tracking-[0.22em] px-2 py-1 border-l border-ink/20 ${tabMode === "mine" ? "bg-stamp-red text-paper" : "text-ink-soft hover:text-stamp-red"}`}
                                >
                                    My tracks
                                </button>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {tabMode === "station" && (
                                <button
                                    onClick={commissionAll}
                                    disabled={commissioning || (catalogueSize > 0 && commissionedCount >= catalogueSize)}
                                    className="inline-flex items-center gap-2 border border-ink/30 hover:border-stamp-red hover:text-stamp-red disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1 font-heading uppercase text-[10px] tracking-[0.25em]"
                                    data-testid="radio-commission-all"
                                    title={
                                        catalogueSize > 0 && commissionedCount >= catalogueSize
                                            ? "Every catalogue prompt is already on file"
                                            : `Commission ${Math.max(0, catalogueSize - commissionedCount)} new songs from the catalogue`
                                    }
                                >
                                    {commissioning ? (
                                        <>
                                            <Loader2 className="w-3 h-3 animate-spin" /> Dispatching…
                                        </>
                                    ) : catalogueSize > 0 && commissionedCount >= catalogueSize ? (
                                        <>
                                            <CheckCircle2 className="w-3 h-3" /> Catalogue full · {commissionedCount}/{catalogueSize}
                                        </>
                                    ) : (
                                        <>
                                            <Sparkles className="w-3 h-3" /> Commission {Math.max(0, catalogueSize - commissionedCount)} · {commissionedCount}/{catalogueSize}
                                        </>
                                    )}
                                </button>
                            )}
                            <button
                                onClick={exportLibrary}
                                disabled={!visibleTracks.length}
                                className="inline-flex items-center gap-2 border border-ink/30 hover:border-stamp-red hover:text-stamp-red disabled:opacity-40 px-3 py-1 font-heading uppercase text-[10px] tracking-[0.25em]"
                                data-testid="radio-export"
                                title="Download as plain text (for No. 2)"
                            >
                                <FileDown className="w-3 h-3" /> Export
                            </button>
                        </div>
                    </div>
                    {commissionMsg && (
                        <div
                            className="mb-3 label-typed text-[11px] italic text-ink-soft border-l-2 border-stamp-red pl-2"
                            data-testid="radio-commission-msg"
                        >
                            {commissionMsg}
                        </div>
                    )}
                    {/* Iter 31.80 — Brand filter chips on Station tab only */}
                    {tabMode === "station" && stationTotal > 0 && (
                        <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid="radio-brand-filters">
                            {[
                                { key: null, label: "All", count: stationTotal },
                                { key: "spy_finance", label: "Spy Finance", count: brandCounts.spy_finance || 0 },
                                { key: "garys_shed", label: "The Workshop", count: brandCounts.garys_shed || 0 },
                                { key: "prisoner", label: "Prisoner", count: brandCounts.prisoner || 0 },
                                { key: "crossover", label: "Crossover", count: brandCounts.crossover || 0 },
                            ].filter((chip) => chip.key === null || chip.count > 0).map((chip) => {
                                const active = brandFilter === chip.key;
                                return (
                                    <button
                                        key={chip.key || "all"}
                                        type="button"
                                        onClick={() => setBrandFilter(chip.key)}
                                        data-testid={`radio-brand-chip-${chip.key || "all"}`}
                                        className={`font-heading uppercase text-[10px] tracking-[0.22em] px-2.5 py-1 border ${
                                            active
                                                ? "bg-stamp-red text-paper border-stamp-red"
                                                : "border-ink/25 text-ink-soft hover:border-stamp-red hover:text-stamp-red"
                                        }`}
                                    >
                                        {chip.label} <span className="opacity-70 ml-1">{chip.count}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    {visibleTracks.length === 0 ? (
                        <div className="text-ink-faint label-typed italic">
                            {tabMode === "station"
                                ? "Station catalogue is empty. Commission tracks from Billing → Radio commissions."
                                : "No personal tracks. Upload via Radio admin or via Mureka generations."}
                        </div>
                    ) : (
                        <table className="w-full font-body text-sm table-fixed" data-testid="radio-track-list">
                            <thead>
                                <tr className="label-typed border-b border-ink/20">
                                    <th className="text-left py-1 px-1 w-8"></th>
                                    <th className="text-left py-1 px-1">Track</th>
                                    <th className="text-right py-1 px-2 w-10" title="Plays">P</th>
                                    <th className="text-right py-1 px-2 w-10" title="Skips">S</th>
                                    <th className="text-right py-1 px-2 w-10" title="Replays">R</th>
                                    <th className="text-right py-1 px-1 w-8"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleTracks.map((t) => (
                                    <tr key={t.id} className="border-b border-ink/10 last:border-0" data-testid={`radio-track-${t.id}`}>
                                        <td className="py-2 px-1">
                                            <button
                                                onClick={() => playAt(queue.findIndex((q) => q.kind === "track" && q.ref.id === t.id))}
                                                className="text-ink-soft hover:text-stamp-red"
                                                title="Play"
                                            >
                                                <Play className="w-3.5 h-3.5" />
                                            </button>
                                        </td>
                                        <td className="py-2 px-1 min-w-0 max-w-0">
                                            <div className="flex items-center gap-2">
                                                {t.generated && <span className="stamp-red text-[8px] px-1 border border-stamp-red flex-shrink-0">MUREKA</span>}
                                                <span className="text-ink truncate min-w-0" title={t.title}>{t.title}</span>
                                            </div>
                                            <div className="label-typed text-[9px] truncate" title={`${t.uploaded_at || ""} · ${t.size_bytes} B`}>
                                                {(t.size_bytes / 1024).toFixed(0)} KB · {t.uploaded_at?.slice(0, 10)}
                                                {t.last_played_at && ` · last ${t.last_played_at.slice(0, 10)}`}
                                            </div>
                                        </td>
                                        <td className="py-2 px-2 text-right font-heading text-stamp-green">{t.play_count || 0}</td>
                                        <td className="py-2 px-2 text-right font-heading text-ink-soft">{t.skip_count || 0}</td>
                                        <td className="py-2 px-2 text-right font-heading text-stamp-red">{t.replay_count || 0}</td>
                                        <td className="py-2 px-1 text-right">
                                            <button
                                                onClick={() => prepDistribution(t.id, t.title)}
                                                className="text-ink-soft hover:text-stamp-red mr-2"
                                                title="Prep distribution bundle for DistroKid / CD Baby"
                                                data-testid={`radio-track-distribute-${t.id}`}
                                            >
                                                <Package className="w-3.5 h-3.5" />
                                            </button>
                                            <button
                                                onClick={() => remove(t.id)}
                                                className="text-ink-soft hover:text-stamp-red"
                                                title="Remove"
                                                data-testid={`radio-track-delete-${t.id}`}
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Self-writing weekly bulletin */}
            {bulletin?.text && bulletin?.stats?.tracks_total > 0 && (
                <div className="mt-6 paper-card tape-corner p-5 tilt-r" data-testid="radio-bulletin">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2 label-typed tw-prefix">
                            <BarChart2 className="w-3 h-3" /> Weekly bulletin · self-writing
                        </div>
                        <span className="stamp-blue text-[10px]">
                            {bulletin.voice?.label} · past {bulletin.window_days}d
                        </span>
                    </div>
                    <p className="font-body text-sm text-ink leading-relaxed">{bulletin.text}</p>
                    <div className="mt-3 label-typed text-[9px]">
                        {bulletin.stats.events_total} event{bulletin.stats.events_total === 1 ? "" : "s"} across {bulletin.stats.tracks_total} track{bulletin.stats.tracks_total === 1 ? "" : "s"} · queued at start of rotation
                    </div>
                </div>
            )}

            {/* Drops preview */}
            {drops.length > 0 && (
                <div className="mt-6 paper-card p-4" data-testid="radio-drops">
                    <div className="label-typed tw-prefix mb-3">Next drops (rotating voice cast)</div>
                    <ul className="space-y-2">
                        {drops.slice(0, 5).map((d) => (
                            <li key={d.entry_id} className="flex items-start gap-2 font-body text-sm text-ink border-b border-ink/10 pb-2 last:border-0">
                                <span className="stamp-blue text-[10px] pt-0.5">{d.voice?.label}</span>
                                <span className="flex-1">{d.text}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            </div>{/* /radio-songs-surface */}
        </Shell>
    );
}
